const popupSelector = '[data-maryssil-promotion-popup]';
const shownSessionKey = 'maryssil-promotion-popup-shown-v1';
const closedUntilKey = 'maryssil-promotion-popup-closed-until-v1';
const convertedUntilKey = 'maryssil-promotion-popup-converted-until-v1';
const defaultScrollThreshold = 0.4;
const defaultCloseCooldownDays = 3;
const defaultConversionCooldownDays = 30;
const mobileTimerMilliseconds = 25_000;
const localTestQueryParameter = 'maryssil_popup_test';

function getStorage(storageName) {
  try {
    return window[storageName];
  } catch (_) {
    return null;
  }
}

function readFutureTimestamp(storage, key) {
  if (!storage) return 0;

  try {
    const timestamp = Number(storage.getItem(key) || 0);
    if (Number.isFinite(timestamp) && timestamp > Date.now()) return timestamp;
    if (timestamp) storage.removeItem(key);
  } catch (_) {
    return 0;
  }

  return 0;
}

function writeFutureTimestamp(storage, key, timestamp) {
  try {
    storage?.setItem(key, String(timestamp));
  } catch (_) {
    // Private browsing can deny storage. The in-memory state still protects
    // this page view and the popup remains usable.
  }
}

function readSessionMarker(storage) {
  try {
    return storage?.getItem(shownSessionKey) === '1';
  } catch (_) {
    return false;
  }
}

function writeSessionMarker(storage) {
  try {
    storage?.setItem(shownSessionKey, '1');
  } catch (_) {
    // The in-memory state is the fallback when sessionStorage is unavailable.
  }
}

function removeStorageValue(storage, key) {
  try {
    storage?.removeItem(key);
  } catch (_) {
    // Private browsing can deny storage. The regular in-memory guards still apply.
  }
}

function isLocalTestRequest() {
  const localHost = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';
  return localHost && new URLSearchParams(window.location.search).get(localTestQueryParameter) === '1';
}

function numberFromDataset(element, key, fallback) {
  const value = Number(element.dataset[key]);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function isMobileViewport() {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 749px)').matches
    : window.innerWidth <= 749;
}

function initializeMaryssilPromotionPopup() {
  const popup = document.querySelector(popupSelector);
  if (!(popup instanceof HTMLDialogElement) || popup.dataset.ready === 'true') return;
  if (!['product', 'collection', 'index'].includes(popup.dataset.pageType)) return;

  popup.dataset.ready = 'true';

  const sessionStorage = getStorage('sessionStorage');
  const localStorage = getStorage('localStorage');
  const localTestMode = isLocalTestRequest();
  const form = popup.querySelector('form');
  const emailInput = popup.querySelector('input[type="email"]');
  const submitButton = popup.querySelector('[data-maryssil-promotion-popup-submit]');
  const successMessage = popup.querySelector('[data-maryssil-promotion-popup-success]');
  const errorMessage = popup.querySelector('[data-maryssil-promotion-popup-error]');
  const postbackMessage = successMessage || errorMessage;
  const threshold = Math.min(
    0.45,
    Math.max(0.35, numberFromDataset(popup, 'scrollThreshold', defaultScrollThreshold))
  );
  const closeCooldownDays = numberFromDataset(popup, 'closeCooldownDays', defaultCloseCooldownDays);
  const conversionCooldownDays = numberFromDataset(popup, 'conversionCooldownDays', defaultConversionCooldownDays);
  const closeCooldownMilliseconds = closeCooldownDays * 24 * 60 * 60 * 1000;
  const conversionCooldownMilliseconds = conversionCooldownDays * 24 * 60 * 60 * 1000;

  let sessionShown = readSessionMarker(sessionStorage);
  let memoryShown = false;
  let interactionObserved = false;
  let submitting = false;
  let opener = null;
  let scrollMarker = null;
  let intersectionObserver = null;
  let resizeObserver = null;
  let mobileTimer = null;
  let viewportMediaQuery = null;
  let viewportChangeHandler = null;
  let desktopExitIntentAttached = false;

  if (localTestMode) {
    removeStorageValue(sessionStorage, shownSessionKey);
    removeStorageValue(localStorage, closedUntilKey);
    removeStorageValue(localStorage, convertedUntilKey);
  }

  const hasCooldown = () => Boolean(
    readFutureTimestamp(localStorage, closedUntilKey) ||
    readFutureTimestamp(localStorage, convertedUntilKey)
  );

  const hasBlockingInterface = () => Boolean(
    document.querySelector(
      'theme-drawer[open], dialog[open]:not([data-maryssil-promotion-popup])'
    )
  );

  const canOpen = () => {
    if (memoryShown || sessionShown || popup.open || document.visibilityState !== 'visible') return false;
    if (hasBlockingInterface()) return false;
    if (localTestMode) return true;
    if (successMessage || hasCooldown()) return false;
    return true;
  };

  const markShown = () => {
    memoryShown = true;
    sessionShown = true;
    writeSessionMarker(sessionStorage);
  };

  const restoreFocus = () => {
    if (!(opener instanceof HTMLElement) || !opener.isConnected) return;
    try {
      opener.focus({ preventScroll: true });
    } catch (_) {
      opener.focus();
    }
    opener = null;
  };

  const removeTriggerObservers = () => {
    intersectionObserver?.disconnect();
    resizeObserver?.disconnect();
    intersectionObserver = null;
    resizeObserver = null;
    if (scrollMarker?.isConnected) scrollMarker.remove();
    scrollMarker = null;
    if (mobileTimer !== null) window.clearTimeout(mobileTimer);
    mobileTimer = null;
    for (const [eventName, handler] of interactionListeners) {
      document.removeEventListener(eventName, handler);
    }
    if (desktopExitIntentAttached) {
      document.removeEventListener('mouseout', exitIntentHandler);
      desktopExitIntentAttached = false;
    }
    if (viewportMediaQuery && viewportChangeHandler) {
      if (typeof viewportMediaQuery.removeEventListener === 'function') {
        viewportMediaQuery.removeEventListener('change', viewportChangeHandler);
      } else {
        viewportMediaQuery.removeListener?.(viewportChangeHandler);
      }
    }
    viewportMediaQuery = null;
    viewportChangeHandler = null;
  };

  const markClosed = () => {
    if (submitting || successMessage) return;
    writeFutureTimestamp(localStorage, closedUntilKey, Date.now() + closeCooldownMilliseconds);
  };

  const closePopup = (reason = 'dismiss') => {
    if (!popup.open) return;
    if (reason !== 'submit') markClosed();
    popup.close(reason);
  };

  const openPopup = (reason, { force = false } = {}) => {
    if (!force && !canOpen()) return;
    if (!popup.open) {
      markShown();
      removeTriggerObservers();
      opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      popup.dataset.trigger = reason;
      document.documentElement.classList.add('maryssil-promotion-popup-open');

      if (typeof popup.showModal === 'function') popup.showModal();
      else popup.setAttribute('open', '');
    }

    window.requestAnimationFrame(() => {
      const focusTarget = postbackMessage || emailInput;
      try {
        focusTarget?.focus({ preventScroll: true });
      } catch (_) {
        focusTarget?.focus();
      }
    });
  };

  const markerIsReached = () => (
    scrollMarker instanceof HTMLElement &&
    scrollMarker.getBoundingClientRect().top <= window.innerHeight
  );

  const positionScrollMarker = () => {
    if (!(scrollMarker instanceof HTMLElement)) return;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const targetScroll = maxScroll * threshold;
    scrollMarker.style.top = `${Math.max(0, targetScroll + window.innerHeight - 2)}px`;
  };

  const interactionBelongsToBlockingInterface = (event) => {
    const target = event.target;
    return target instanceof Element && Boolean(
      target.closest('theme-drawer, dialog, [aria-haspopup="dialog"]')
    );
  };

  const onInteraction = (event) => {
    interactionObserved = true;
    if (interactionBelongsToBlockingInterface(event)) return;
    window.requestAnimationFrame(() => {
      if (markerIsReached()) openPopup('scroll');
    });
  };

  const interactionListeners = [
    ['pointerdown', onInteraction],
    ['keydown', onInteraction],
    ['touchstart', onInteraction],
    ['wheel', onInteraction],
  ];

  const exitIntentHandler = (event) => {
    if (!interactionObserved || isMobileViewport() || event.relatedTarget !== null || event.clientY > 8) return;
    openPopup('exit-intent');
  };

  const attachDesktopExitIntent = () => {
    if (desktopExitIntentAttached) return;
    document.addEventListener('mouseout', exitIntentHandler, { passive: true });
    desktopExitIntentAttached = true;
  };

  const detachDesktopExitIntent = () => {
    if (!desktopExitIntentAttached) return;
    document.removeEventListener('mouseout', exitIntentHandler);
    desktopExitIntentAttached = false;
  };

  const startMobileTimer = () => {
    if (mobileTimer !== null) return;
    mobileTimer = window.setTimeout(() => {
      mobileTimer = null;
      if (interactionObserved) openPopup('mobile-interaction-timer');
    }, mobileTimerMilliseconds);
  };

  const syncViewportTriggers = () => {
    if (isMobileViewport()) {
      detachDesktopExitIntent();
      startMobileTimer();
      return;
    }

    if (mobileTimer !== null) {
      window.clearTimeout(mobileTimer);
      mobileTimer = null;
    }
    attachDesktopExitIntent();
  };

  popup.addEventListener('click', (event) => {
    if (event.target === popup) closePopup();
  });

  popup.querySelector('[data-maryssil-promotion-popup-close]')?.addEventListener('click', () => closePopup());

  popup.addEventListener('cancel', (event) => {
    event.preventDefault();
    closePopup();
  });

  popup.addEventListener('close', () => {
    if (!submitting && !successMessage && memoryShown) {
      writeFutureTimestamp(localStorage, closedUntilKey, Date.now() + closeCooldownMilliseconds);
    }
    document.documentElement.classList.remove('maryssil-promotion-popup-open');
    restoreFocus();
  });

  form?.addEventListener('submit', () => {
    submitting = true;
    if (!(submitButton instanceof HTMLButtonElement)) return;
    submitButton.disabled = true;
    submitButton.dataset.originalLabel = submitButton.textContent || '';
    submitButton.textContent = 'Enviando...';
  });

  if (successMessage) {
    writeFutureTimestamp(localStorage, convertedUntilKey, Date.now() + conversionCooldownMilliseconds);
  }

  if (postbackMessage) {
    window.requestAnimationFrame(() => {
      openPopup(successMessage ? 'postback-success' : 'postback-error', { force: true });
    });
    return;
  }

  if (localTestMode) {
    window.requestAnimationFrame(() => openPopup('local-test', { force: true }));
    return;
  }

  scrollMarker = document.createElement('span');
  scrollMarker.dataset.maryssilPromotionScrollMarker = '';
  scrollMarker.setAttribute('aria-hidden', 'true');
  scrollMarker.style.position = 'absolute';
  scrollMarker.style.left = '0';
  scrollMarker.style.width = '1px';
  scrollMarker.style.height = '1px';
  scrollMarker.style.pointerEvents = 'none';
  scrollMarker.style.opacity = '0';
  document.body.append(scrollMarker);
  positionScrollMarker();

  if (typeof IntersectionObserver === 'function') {
    intersectionObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && interactionObserved) openPopup('scroll');
    });
    intersectionObserver.observe(scrollMarker);
  }

  if (typeof ResizeObserver === 'function') {
    resizeObserver = new ResizeObserver(positionScrollMarker);
    resizeObserver.observe(document.documentElement);
  }

  for (const [eventName, handler] of interactionListeners) {
    document.addEventListener(eventName, handler, { passive: eventName !== 'keydown' });
  }

  viewportMediaQuery = typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 749px)')
    : null;
  viewportChangeHandler = syncViewportTriggers;
  syncViewportTriggers();
  if (viewportMediaQuery) {
    if (typeof viewportMediaQuery.addEventListener === 'function') {
      viewportMediaQuery.addEventListener('change', viewportChangeHandler);
    } else {
      viewportMediaQuery.addListener?.(viewportChangeHandler);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMaryssilPromotionPopup, { once: true });
} else {
  initializeMaryssilPromotionPopup();
}
